import moment from 'moment'
import _ from 'lodash'
import * as d3 from 'd3'
import d3Tip from 'd3-tip'

class TemperatureLineChart {
  constructor (el, path) {
    this.el = el
    this._buildSvg()
    this._buildChartContainer()
    this._setupResize()
    this._setupTips()
    this.clipPath = path

    this.legendYWidth = 25
  }

  update (data) {
    this.data = data ? this._prepData(data) : this.data
    this._combineValuations()
    this._draw()
    if (data.disableValue) {
      this.tip.attr('class', 'hidden')
    }
  }

  destroy () {
    // ensure all event listeners are cleaned up
    if (typeof window !== 'undefined')
      window.removeEventListener('resize', this._redrawChart)
  }

  _draw () {
    this._calcDimensions()
    this._shiftChartContainer()
    this._calcScales()
    this._axes()
    this._drawGradient()
    this._segments() // Copied from ValueLineChart, requires data transform as is
  }

  _buildSvg () {
    this.svg = d3.select(this.el)
  }

  _buildChartContainer () {
    this.chart = this.svg.append('g')
  }

  _shiftChartContainer () {
    this.chart.attr(
      'transform',
      'translate(' + this.margin.left + ',' + this.margin.top + ')'
    )
  }

  _axes () {
    this._drawXAxis()
    this._drawYAxis()
  }

  _drawLines () {
    let line = d3
      .line()
      .curve(d3.curveCardinal)
      .x(d => this.scaleX(d.date))
      .y(d => this.scaleY(d.value))

    this.chart
      .append('path')
      .datum(this.allValuations)
      .attr('class', 'line')
      .style('stroke', 'url(#temperature-gradient)')
      .attr('d', line)
  }

  _drawGradient () {
    this.chart
      .append('linearGradient')
      .attr('id', 'temperature-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', '100%')
      .selectAll('stop')
      .data([
        { offset: '5%', color: '#FF4545' },
        { offset: '95%', color: '#41B9FA' }
      ])
      .enter()
      .append('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color)
  }

  _segments () {
    let line = d3
      .line()
      .curve(d3.curveNatural)
      .x(d => {
        return this.scaleX(d.date)
      })
      .y((d, i) => {
        return this.scaleY(d.valuation)
      })

    this.segment = this.chart
      .selectAll('.segment')
      .data(this.data.segments)
      .enter()
      .append('g')
      .attr('class', 'segment')

    this.segment
      .filter(d => {
        return d.valuations.length > 1
      })
      .append('path')
      .attr('class', 'line')
      .attr('d', d => {
        return line(d.valuations)
      })
      .style('stroke', 'url(#temperature-gradient)')

    this.segment
      .selectAll('g')
      .data(d => {
        return d.valuations
      })
      .enter()
      .append('g')
      .classed('datapoint', true)
      .call(parentDatapoint => {
        this._addDataPoints(parentDatapoint)
      })
  }

  _addDataPoints (datapoint) {
    this._pointBorders(datapoint)
    this._points(datapoint)
  }

  _points (datapoint) {
    datapoint
      .append('circle')
      .classed('point', true)
      .attr('cx', d => {
        return this.scaleX(d.date)
      })
      .attr('cy', d => {
        return this.scaleY(d.valuation)
      })
      .attr('r', 3.5)
      .style('fill', 'url(#temperature-gradient)')
    // .on('mouseover', this.tip.show)
    // .on('mouseout', this.tip.hide)
  }

  _pointBorders (datapoint) {
    datapoint
      .filter((d, i, nodes) => {
        return i === this.allValuations.length - 1
      })
      .append('circle')
      .classed('point-border', true)
      .style('stroke', 'url(#temperature-gradient)')
      .attr('cx', d => {
        return this.scaleX(d.date)
      })
      .attr('cy', d => {
        return this.scaleY(d.valuation)
      })
      .attr('r', 7.5)
  }

  _drawXAxis () {
    const xAxis = d3
      .axisBottom(this.scaleX)
      .tickFormat(d3.timeFormat("%b '%y"))
      .tickValues(this.allValuations.map(({ date }) => date))

    this.chart
      .append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', 'translate(0,' + this.chartHeight + ')')
      .call(xAxis)
  }

  _drawYAxis () {
    if (this.data.disableValue) return
    const yAxis = d3
      .axisRight(this.scaleY)
      .tickSize(this.outerWidth)
      .tickValues([0, 25, 50, 75, 100])

    // Add to outer svg because it's outside the graph on the left
    // Prepend instead of append so that it's under the chart
    this.yAxis = this.svg
      .insert('g', ':first-child')
      .attr('class', 'axis axis--y')
      .attr('transform', `translate(0, ${this.margin.top})`)
      .call(yAxis)

    this.yAxis
      .selectAll('line')
      .attr('transform', d => `translate(0, ${this.scaleY(d) * -1})`)
      .attr('y1', d => this.scaleY(d))
      .attr('y2', d => this.scaleY(d))
      .style('stroke-width', '2')
      .style('stroke-dasharray', '7 5')
      .style('stroke', 'url(#temperature-gradient)')
      .style('opacity', d => Math.abs(50 - d) / 150 + 0.5)

    this.yAxis
      .selectAll('text')
      .attr('x', 0)
      .style('display', 'none')

    this.legendY = this.yAxis
      .insert('g', ':first-child')
      .attr('transform', `translate(12, 0)`)

    this.legendY
      .insert('line', ':first-child')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', -1)
      .attr('y2', this.chartHeight + 2)
      .style('stroke-width', this.legendYWidth)
      .style('stroke', 'url(#temperature-gradient)')

    const cosmetics = {
      fill: 'black',
      fontWeight: '700',
      fontSize: '12px',
      textTransform: 'uppercase'
    }

    this.legendY
      .append('g')
      .attr('transform', `translate(4, ${this.chartHeight - 5})`)
      .append('text')
      .attr('fill', cosmetics.fill)
      .attr('textLength', `${this.chartHeight * 0.4}`)
      .attr('transform', 'rotate(-90)')
      .style('font-size', cosmetics.fontSize)
      .style('font-weight', cosmetics.fontWeight)
      .style('text-transform', cosmetics.textTransform)
      .html('Easier To Buy')

    this.legendY
      .append('g')
      .attr('transform', `translate(4, ${this.chartHeight * 0.4 + 10})`)
      .append('text')
      .attr('fill', 'black')
      .attr('textLength', `${this.chartHeight * 0.4}`)
      .attr('transform', 'rotate(-90)')
      .style('font-size', cosmetics.fontSize)
      .style('font-weight', cosmetics.fontWeight)
      .style('text-transform', cosmetics.textTransform)
      .html('Easier To Sell')
  }

  _setupTips () {
    return // better stuff in our MarketSpotlightChart

    // Lame hack for tests to run. We'll need to dev our own tooltip at some point.
    if (!d3Tip) {
      this.tip = { show: () => {}, hide: () => {} }
      return
    }

    this.tip = d3Tip()
      .attr('class', 'chart-tip temperature-chart-tip')
      .offset([-18, 0])
      .html(d => {
        const dateString = moment(d.date).format("MMM 'YY")
        let market, color

        if (d.valuation > 80) {
          market = "Strong seller's advantage"
          color = '#FF4545'
        } else if (d.valuation > 60) {
          market = "Slight seller's advantage"
          color = '#CE6474'
        } else if (d.valuation > 40) {
          market = 'Balanced market'
          color = '#A27F9F'
        } else if (d.valuation > 20) {
          market = "Slight buyer's advantage"
          color = '#8391BB'
        } else {
          market = "Strong buyer's advantage"
          color = '#41B9FA'
        }

        return `<div>${dateString}</div><div style="color: ${color}">${market}</div>`
      })

    this.chart.call(this.tip)
  }

  _calcDimensions () {
    this.margin = {
      top: 0,
      right: 25,
      bottom: 30,
      left: this.legendYWidth + 15
    }
    this.outerWidth = this.el.getBoundingClientRect().width
    this.outerHeight = this.el.getBoundingClientRect().height
    this.chartWidth = this.outerWidth - this.margin.left - this.margin.right
    this.chartHeight = this.outerHeight - this.margin.top - this.margin.bottom
  }

  _calcScales () {
    this.scaleX = d3
      .scaleTime()
      .domain(this.xDomain)
      .range([0, this.chartWidth])
    this.scaleY = d3
      .scaleLinear()
      .domain(this._getPaddedYDomain())
      .range([this.chartHeight, 0])
  }

  _getValues () {
    const data = _.cloneDeep(this.data)
    this.allValues = data.datapoints
  }

  _getPaddedYDomain () {
    let min = 0
    let max = 100

    return [min, max]
  }

  _setupResize () {
    if (this._redrawChart) return

    this._redrawChart = _.debounce(
      () => {
        this.chart.selectAll('*').remove()
        if (this.yAxis) {
          this.yAxis.remove()
        }
        this._draw()
      },
      250,
      { leading: false }
    )

    if (typeof window !== 'undefined')
      window.addEventListener('resize', this._redrawChart)
  }

  _combineValuations () {
    const data = _.cloneDeep(this.data)
    let valuations = []
    data.segments.forEach(
      segment => (valuations = valuations.concat(segment.valuations))
    )
    this.allValuations = valuations
  }

  _prepData (data) {
    let transformedData = _.cloneDeep(data)

    return transformedData
  }

  get xDomain () {
    return d3.extent(this.allValuations, d => {
      return d.date
    })
  }
}

export default TemperatureLineChart
